import { verifyWalletAccount } from '@dynamic-labs-sdk/client';
import { assertDefined, getDefaultClient } from '@dynamic-labs-sdk/client/core';
import type { WalletConnectConnectionResult } from '@dynamic-labs-sdk/wallet-connect';

import { connectWithWalletConnectSolana } from '../connectWithWalletConnectSolana';

/**
 * Connects to a WalletConnect Solana wallet and verifies ownership through message signing.
 *
 * This function will first perform a regular connect and then verify the wallet accounts via
 * verifyWalletAccount.
 *
 * **Note on connecting and verifying in a single step:**
 * Currently WalletConnect does NOT support the authenticate method for any non eip155 chains.
 * This means there is no way to perform connection and verification in a single step.
 * This can easily be tested by attempting to call `signClient.authenticate` with a solana namespace
 * and checking whether WalletConnect will throw the following error:
 * ```
 * 'Only eip155 namespace is supported for authenticated sessions. Please use .connect() for non-eip155 chains.'
 * ```
 *
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to the connected and verified wallet account.
 */
export const connectAndVerifyWithWalletConnectSolana = async (
  client = getDefaultClient()
): Promise<WalletConnectConnectionResult> => {
  const { approval, uri } = await connectWithWalletConnectSolana(
    // We don't want to add the wallet accounts to the dynamic's wallet accounts state
    // while we are still verifying them.
    { addToDynamicWalletAccounts: false },
    client
  );

  assertDefined(
    uri,
    'Failed to establish WalletConnect session: no URI returned'
  );

  /**
   * Once the approval promise resolves, the WalletConnect session is established
   * between the wallet application and the dapp (this app).
   */
  const walletAccountsPromise = approval().then(async ({ walletAccounts }) => {
    // This should not be concurrent because the first will be a verify and the rest will be a link
    for (const walletAccount of walletAccounts) {
      await verifyWalletAccount({ walletAccount }, client);
    }

    return { walletAccounts };
  });

  return { approval: () => walletAccountsPromise, uri };
};
